/**
 * Dashboard — the App Owner's live overview: headline numbers, sign-up
 * trend, plan / status / billing-channel / platform breakdowns, studios
 * that need attention, recent payments and newest studios.
 *
 * Data: `adminGetDashboard` (one call, server-side aggregation).
 */
import {
  Anchor,
  Badge,
  Divider,
  Grid,
  Group,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBuilding,
  IconCurrencyRupee,
  IconFolder,
  IconGift,
  IconReceipt,
  IconUserCheck,
  IconUsers,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { RecalculateStorageButton } from '@/components/RecalculateStorageButton';
import {
  ChannelBadge,
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  MixBars,
  PageHeader,
  PlatformBadge,
  RefreshButton,
  RelTime,
  SectionCard,
  StatCard,
  StorageBar,
  StudioAvatar,
  TierBadge,
  useAdminData,
} from '@/components/ui';
import type { Dashboard as DashboardData, StudioRef } from '@/lib/adminTypes';
import { callAdmin } from '@/lib/firebase';
import {
  CHANNEL_COLOR,
  CHANNEL_LABEL,
  EVENT_LABEL,
  PLATFORM_COLOR,
  PLATFORM_LABEL,
  STATUS_COLOR,
  STATUS_LABEL,
  STORE_LABEL,
  TIER_COLOR,
  TIER_LABEL,
  fmtBytes,
  fmtInr,
  fmtMoney,
  fmtPhone,
  fmtRelative,
  label,
} from '@/lib/format';

const ATTENTION: Array<{ key: keyof Omit<DashboardData['attention'], 'unmatchedStoreEvents'>; title: string; color: string }> = [
  { key: 'pastDue', title: 'Payment failed', color: 'orange' },
  { key: 'endingSoon', title: 'Auto-renew off — ending in 14 days', color: 'yellow' },
  { key: 'recentlyExpired', title: 'Expired in the last 30 days', color: 'red' },
  { key: 'overLimit', title: 'Over plan limits (grace / paused / view-only)', color: 'grape' },
  { key: 'storageFull', title: 'Storage 80% or more used', color: 'orange' },
  { key: 'compEnding', title: 'Comp plans ending in 14 days', color: 'violet' },
  { key: 'compOverdue', title: 'Comp plans past their end date', color: 'gray' },
];

export function Dashboard() {
  const nav = useNavigate();
  const { data, error, loading, reload } = useAdminData(() => callAdmin.getDashboard({}));
  const [platformView, setPlatformView] = useState<'signup' | 'last'>('signup');

  const chart = useMemo(
    () =>
      (data?.signupsByDay ?? []).map((d) => ({
        ...d,
        label: new Date(`${d.date}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      })),
    [data],
  );

  if (error && !data) return <ErrorBlock message={error} onRetry={reload} />;
  if (!data) return <LoadingBlock label="Crunching the numbers…" />;

  const t = data.totals;
  const inr = data.revenue.INR;
  const attentionCount = ATTENTION.reduce((n, a) => n + data.attention[a.key].length, 0);
  const paying = data.payingByStore;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`Live overview · updated ${fmtRelative(data.generatedAt)}`}
        actions={<RefreshButton onClick={reload} loading={loading} />}
      />

      <Stack gap="lg">
        <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }} spacing="md">
          <StatCard
            label="Studios"
            value={t.studios}
            hint={`+${t.newStudios7d} this week · +${t.newStudios30d} in 30 days`}
            icon={<IconBuilding size={22} />}
            onClick={() => nav('/studios')}
          />
          <StatCard
            label="Users"
            value={t.users}
            hint={`+${t.newUsers7d} this week · ${t.activeUsers7d} active this week`}
            icon={<IconUsers size={22} />}
            color="indigo"
            onClick={() => nav('/users')}
          />
          <StatCard
            label="Paying studios"
            value={t.payingStudios}
            hint={`App Store ${paying.app_store ?? 0} · Google Play ${paying.play_store ?? 0} · Web ${paying.web ?? 0}`}
            icon={<IconUserCheck size={22} />}
            color="teal"
            onClick={() => nav('/studios?channel=paying')}
          />
          <StatCard
            label="Est. monthly revenue"
            value={fmtInr(t.estMrrInr)}
            hint={
              inr
                ? `Received last 30 days: ${fmtInr(inr.last30)} (${inr.count30} payments)`
                : 'From list prices of active paid plans'
            }
            icon={<IconCurrencyRupee size={22} />}
            color="green"
            onClick={() => nav('/billing')}
          />
          <StatCard
            label="Comp / trial studios"
            value={t.compStudios}
            hint="Plans given from admin"
            icon={<IconGift size={22} />}
            color="grape"
            onClick={() => nav('/studios?channel=comp')}
          />
          <StatCard
            label="Free studios"
            value={t.freeStudios}
            hint="Upgrade candidates"
            icon={<IconBuilding size={22} />}
            color="gray"
            onClick={() => nav('/studios?channel=free')}
          />
          <StatCard
            label="Projects"
            value={t.projects}
            hint={`${t.activeUsers30d} users active in 30 days`}
            icon={<IconFolder size={22} />}
            color="cyan"
          />
          <StatCard
            label="Needs attention"
            value={attentionCount}
            hint={
              data.attention.unmatchedStoreEvents > 0
                ? `${data.attention.unmatchedStoreEvents} store event(s) not matched to a plan`
                : 'Past due, expiring, over limit'
            }
            icon={<IconAlertTriangle size={22} />}
            color={attentionCount > 0 ? 'orange' : 'gray'}
          />
        </SimpleGrid>

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <SectionCard title="Sign-ups" subtitle="New studios and users per day — last 30 days">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chart} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E9EEF3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={3} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: '#F1F5F9' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="studios" name="Studios" fill="#2172C0" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="users" name="Users" fill="#A9CCED" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <SectionCard title="Plans" subtitle="Plan each studio is on now">
              <MixBars
                data={data.planMix}
                labels={TIER_LABEL}
                colors={TIER_COLOR}
                order={['free', 'solo', 'studio', 'studio_plus', 'agency']}
              />
            </SectionCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <SectionCard title="Subscription status" subtitle="All studios">
              <MixBars
                data={data.statusMix}
                labels={STATUS_LABEL}
                colors={STATUS_COLOR}
                order={['active', 'trialing', 'past_due', 'cancelled', 'expired']}
              />
            </SectionCard>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <SectionCard title="Billing channel" subtitle="Where each studio's plan comes from">
              <MixBars
                data={data.channelMix}
                labels={CHANNEL_LABEL}
                colors={CHANNEL_COLOR}
                order={['app_store', 'play_store', 'web', 'comp', 'trial', 'free']}
              />
            </SectionCard>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <SectionCard
              title="Platforms"
              subtitle={platformView === 'signup' ? 'Where users signed up' : 'Where users were last active'}
              action={
                <SegmentedControl
                  size="xs"
                  value={platformView}
                  onChange={(v) => setPlatformView(v as 'signup' | 'last')}
                  data={[
                    { label: 'Sign-up', value: 'signup' },
                    { label: 'Last used', value: 'last' },
                  ]}
                />
              }
            >
              <MixBars
                data={platformView === 'signup' ? data.signupPlatformMix : data.lastPlatformMix}
                labels={PLATFORM_LABEL}
                colors={PLATFORM_COLOR}
                order={['ios', 'android', 'web', 'mobile', 'unknown']}
              />
              <Text size="xs" c="dimmed" mt="sm">
                Platform tracking started with app v1.0.19 — older accounts show as "Not recorded" or
                "Mobile app" until they open the updated app.
              </Text>
            </SectionCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12 }}>
            <SectionCard
              title="Storage"
              subtitle={
                data.storage.report?.generatedAt
                  ? `Files in cloud storage · recounted ${fmtRelative(data.storage.report.generatedAt)} (nightly at 3 AM)`
                  : 'Files in cloud storage · not recounted yet — press Recalculate'
              }
              action={<RecalculateStorageButton onDone={reload} />}
            >
              <Grid gutter="xl">
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Stack gap="xs">
                    <Text fw={750} style={{ fontSize: 28, letterSpacing: -0.5 }}>
                      {fmtBytes(data.storage.usedBytes)}
                    </Text>
                    <Text size="sm" c="dimmed">
                      used by studios · {data.storage.files.toLocaleString('en-IN')} files
                    </Text>
                    {data.storage.report ? (
                      <Text size="xs" c="dimmed">
                        Whole bucket {fmtBytes(data.storage.report.totalBytes)} (
                        {data.storage.report.objects.toLocaleString('en-IN')} files) — includes{' '}
                        {fmtBytes(data.storage.report.appBytes)} feedback screenshots and{' '}
                        {fmtBytes(data.storage.report.orphanBytes)} in {data.storage.report.orphanFiles} orphaned
                        file(s) from deleted items, which aren't charged to any studio.
                      </Text>
                    ) : null}
                    <Text size="xs" c="dimmed">
                      Storage limits are tracked but not enforced — no studio is blocked from uploading.
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 8 }}>
                  {data.storage.top.length === 0 ? (
                    <EmptyState title="No storage counted yet" body="Press Recalculate storage to count every studio's files." />
                  ) : (
                    <Stack gap="sm">
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: 0.4 }}>
                        Largest studios
                      </Text>
                      {data.storage.top.map((t) => (
                        <Group key={t.id} wrap="nowrap" gap="md">
                          <Anchor component={Link} to={`/studios/${t.id}`} size="sm" fw={600} w={200} truncate>
                            {t.name}
                          </Anchor>
                          <div style={{ flex: 1 }}>
                            <StorageBar used={t.bytes} limit={t.limit} compact />
                          </div>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </Grid.Col>
              </Grid>
            </SectionCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 7 }}>
            <SectionCard
              title="Needs attention"
              subtitle="Studios to follow up with"
              action={
                attentionCount > 0 ? (
                  <Badge color="orange" variant="light">
                    {attentionCount}
                  </Badge>
                ) : null
              }
            >
              {attentionCount === 0 ? (
                <EmptyState title="All clear" body="No failed payments, expiring plans or over-limit studios." />
              ) : (
                <Stack gap="md">
                  {ATTENTION.filter((a) => data.attention[a.key].length > 0).map((a) => (
                    <Stack key={a.key} gap={6}>
                      <Group gap={6}>
                        <Badge color={a.color} variant="dot" radius="sm">
                          {a.title}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {data.attention[a.key].length}
                        </Text>
                      </Group>
                      {data.attention[a.key].slice(0, 6).map((s) => (
                        <AttentionRow key={`${a.key}-${s.id}`} item={s} />
                      ))}
                    </Stack>
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <SectionCard
              title="Recent payments & subscription events"
              subtitle="App Store, Google Play and web"
              action={
                <Anchor component={Link} to="/billing" size="xs">
                  View all
                </Anchor>
              }
            >
              {data.recentBilling.length === 0 ? (
                <EmptyState title="No billing events yet" />
              ) : (
                <Stack gap={0}>
                  {data.recentBilling.slice(0, 8).map((e, i) => (
                    <div key={e.id}>
                      {i > 0 ? <Divider my={8} color="#EEF2F6" /> : null}
                      <Group justify="space-between" wrap="nowrap" gap="xs">
                        <Stack gap={2} style={{ minWidth: 0 }}>
                          <Text size="sm" fw={600} truncate>
                            {e.orgId ? (
                              <Anchor component={Link} to={`/studios/${e.orgId}`} inherit c="inherit">
                                {e.orgName ?? e.orgId}
                              </Anchor>
                            ) : (
                              '—'
                            )}
                          </Text>
                          <Group gap={6}>
                            <Text size="xs" c={e.warning ? 'red' : 'dimmed'}>
                              {label(EVENT_LABEL, e.type)}
                              {e.tier ? ` · ${label(TIER_LABEL, e.tier)}${e.period ? ` ${e.period}` : ''}` : ''}
                              {e.warning ? ' · not matched' : ''}
                            </Text>
                          </Group>
                        </Stack>
                        <Stack gap={2} align="flex-end">
                          <Group gap={4}>
                            {e.environment === 'SANDBOX' ? (
                              <Badge size="xs" color="yellow" variant="light">
                                Test
                              </Badge>
                            ) : null}
                            <Badge size="xs" variant="light" color={e.store === 'play_store' ? 'green' : e.store === 'razorpay' ? 'blue' : 'dark'}>
                              {label(STORE_LABEL, e.store)}
                            </Badge>
                          </Group>
                          <Text size="xs" c="dimmed">
                            {e.amount !== null ? `${fmtMoney(e.amount, e.currency)} · ` : ''}
                            {fmtRelative(e.at)}
                          </Text>
                        </Stack>
                      </Group>
                    </div>
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 8 }}>
            <SectionCard
              title="Newest studios"
              action={
                <Anchor component={Link} to="/studios" size="xs">
                  All studios
                </Anchor>
              }
              p="lg"
            >
              <Table.ScrollContainer minWidth={640}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Studio</Table.Th>
                      <Table.Th>Owner</Table.Th>
                      <Table.Th>Plan</Table.Th>
                      <Table.Th>Signed up</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.recentStudios.map((s) => (
                      <Table.Tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/studios/${s.id}`)}>
                        <Table.Td>
                          <Group gap="sm" wrap="nowrap">
                            <StudioAvatar name={s.name} logoUrl={s.logoUrl} size={32} />
                            <Stack gap={0}>
                              <Text size="sm" fw={600}>
                                {s.name}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {[s.city, s.state].filter(Boolean).join(', ') || 'No address yet'}
                              </Text>
                            </Stack>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{fmtPhone(s.owner?.phone ?? null)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <TierBadge tier={s.effectiveTier} />
                            {s.channel !== 'free' ? <ChannelBadge channel={s.channel} /> : null}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Stack gap={2}>
                            <RelTime ms={s.createdAt} />
                            <PlatformBadge platform={s.owner?.signupPlatform ?? 'unknown'} />
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </SectionCard>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Stack gap="md" h="100%">
              <SectionCard title="App versions in use" subtitle="Users active in the last 30 days">
                {data.appVersions.length === 0 ? (
                  <EmptyState title="No version data yet" body="Recorded from app v1.0.19 onwards." />
                ) : (
                  <Stack gap={6}>
                    {data.appVersions.map((v) => (
                      <Group key={v.version} justify="space-between">
                        <Text size="sm" tt="capitalize">
                          {v.version.replace('ios', 'iOS')}
                        </Text>
                        <Badge variant="light" color="gray">
                          {v.count}
                        </Badge>
                      </Group>
                    ))}
                  </Stack>
                )}
              </SectionCard>
              <SectionCard title="Operations">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Anchor component={Link} to="/feedback" size="sm">
                      New feedback
                    </Anchor>
                    <Badge color={data.ops.feedbackNew ? 'orange' : 'gray'} variant="light">
                      {data.ops.feedbackNew ?? '—'}
                    </Badge>
                  </Group>
                  <Group justify="space-between">
                    <Anchor component={Link} to="/io-orders" size="sm">
                      New IO service orders
                    </Anchor>
                    <Badge color={data.ops.ioOrdersNew ? 'orange' : 'gray'} variant="light">
                      {data.ops.ioOrdersNew ?? '—'}
                    </Badge>
                  </Group>
                  <Group justify="space-between">
                    <Group gap={6}>
                      <IconReceipt size={14} />
                      <Text size="sm">Received in the last 12 months</Text>
                    </Group>
                    <Text size="sm" fw={600}>
                      {inr ? fmtInr(inr.last365) : '—'}
                    </Text>
                  </Group>
                </Stack>
              </SectionCard>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
}

function AttentionRow({ item }: { item: StudioRef }) {
  return (
    <Group justify="space-between" wrap="nowrap" pl="md">
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Anchor component={Link} to={`/studios/${item.id}`} size="sm" fw={600} truncate>
          {item.name}
        </Anchor>
        <Text size="xs" c="dimmed" truncate>
          {item.note}
        </Text>
      </Stack>
      <Stack gap={0} align="flex-end">
        <Text size="xs">{fmtPhone(item.ownerPhone)}</Text>
        <Text size="xs" c="dimmed">
          {item.at ? fmtRelative(item.at) : ''}
        </Text>
      </Stack>
    </Group>
  );
}
